import { FormattedMessage, useIntl } from 'react-intl'
import { useBlueprintColumns } from '@hooks/useQuery'
import { AGGREGATE_FUNCTIONS } from 'src/constants/queries'
import type {
  GroupNode,
  ColumnConfig,
  AggregateFunction
} from 'src/types/queries'

import './GroupEditor.css'

interface GroupEditorProps {
  node: GroupNode
  onChange: (node: GroupNode) => void
}

export default function GroupEditor({ node, onChange }: GroupEditorProps) {
  const intl = useIntl()
  const { columns } = useBlueprintColumns(node.source)

  const handleUpdateColumn = (
    index: number,
    updates: Partial<ColumnConfig>
  ) => {
    const newColumns = [...node.columns]
    newColumns[index] = { ...newColumns[index], ...updates }
    onChange({ ...node, columns: newColumns })
  }

  const handleAddColumn = () => {
    onChange({ ...node, columns: [...node.columns, { col: '' }] })
  }

  const handleRemoveColumn = (index: number) => {
    const newColumns = node.columns.filter((_, i) => i !== index)
    onChange({ ...node, columns: newColumns })
  }

  const canRemove = node.columns.length > 1

  return (
    <div id="group-editor">
      <h3>
        <FormattedMessage id={`operation-editor.option.${node.type}`} />
      </h3>

      <ul className="columns-list">
        {node.columns.map((colConfig, index) => {
          return (
            <li key={index} className="column">
              <select
                className="fn-select"
                value={colConfig.fn || ''}
                onChange={(e) =>
                  handleUpdateColumn(index, {
                    fn: (e.target.value as AggregateFunction) || undefined
                  })
                }
              >
                <option value="">
                  {intl.formatMessage({
                    id: 'group-editor.fn.groupby'
                  })}
                </option>
                {AGGREGATE_FUNCTIONS.map((fn) => (
                  <option key={fn} value={fn}>
                    {fn.toUpperCase()}
                  </option>
                ))}
              </select>
              <select
                className="col-select"
                value={colConfig.col}
                onChange={(e) =>
                  handleUpdateColumn(index, { col: e.target.value })
                }
              >
                <option disabled hidden value="">
                  {intl.formatMessage({
                    id: 'select-editor.column.placeholder'
                  })}
                </option>
                {columns.map((c, idx) => (
                  <option key={`${c}-${idx}`} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="remove-column"
                onClick={() => handleRemoveColumn(index)}
                disabled={!canRemove}
              >
                <span className="icon icon-bin" />
              </button>
            </li>
          )
        })}
      </ul>

      <button type="button" className="add-column" onClick={handleAddColumn}>
        <span className="icon icon-plus" />
      </button>
    </div>
  )
}
